import React, { useState } from 'react';
import { useSignIn, useClerk } from '@clerk/clerk-react';
import { ArrowRight, Envelope, ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { api, useAuth } from '../../context/AuthContext';

const codeDigits = (value) => value.replace(/\D/g, '').slice(0, 6);

const readError = (error) =>
  error?.errors?.[0]?.longMessage || error?.errors?.[0]?.message || error?.message || 'Something went wrong';

/**
 * One box for everyone: type an email, get a six-digit code, you are in. New addresses are
 * signed up by the same flow, so nobody has to choose between two buttons that feel the same.
 */
export const OtpSignIn = ({ onSignedIn }) => {
  const { isLoaded, signIn } = useSignIn();
  const { setActive, signOut } = useClerk();
  const { completeOtpSession } = useAuth();

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email');
  const [isNewAccount, setIsNewAccount] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const prepareCode = async (address) => {
    const attempt = await signIn.create({ identifier: address });
    const factor = attempt.supportedFirstFactors?.find((f) => f.strategy === 'email_code');
    if (!factor?.emailAddressId) throw new Error('This account cannot sign in with an email code');
    await signIn.prepareFirstFactor({ strategy: 'email_code', emailAddressId: factor.emailAddressId });
  };

  const sendCode = async (event) => {
    event?.preventDefault();
    if (!isLoaded) return;
    setBusy(true);
    setError('');
    const address = email.trim().toLowerCase();

    try {
      // The account is created server-side when the address is new, so from here there is only
      // ever one flow: Clerk emails a code and we check it.
      const started = await api.post('/auth/otp/start', { email: address });
      setIsNewAccount(!started.data.account_exists);

      try {
        await prepareCode(address);
      } catch (createError) {
        // A Clerk session can outlive ours and then Clerk refuses with "already signed in".
        // Drop it and come back to a clean sign-in screen rather than leaving someone stuck.
        // Clerk raises this one as a plain Error, not as its usual `errors` array, so look at both.
        const text = [
          createError?.message,
          ...(createError?.errors || []).map((e) => `${e.code} ${e.message} ${e.longMessage}`),
        ]
          .filter(Boolean)
          .join(' ');
        const alreadySignedIn = /already signed in|identifier_already_signed_in/i.test(text);
        if (!alreadySignedIn) throw createError;
        await signOut({ redirectUrl: '/login' });
        return;
      }

      setStep('code');
    } catch (sendError) {
      setError(sendError.response?.data?.detail || readError(sendError));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (event) => {
    event.preventDefault();
    if (code.length !== 6) {
      setError('Enter the 6-digit code');
      return;
    }
    setBusy(true);
    setError('');

    try {
      const result = await signIn.attemptFirstFactor({ strategy: 'email_code', code });
      if (result.status !== 'complete') {
        setError('That code did not complete the sign-in. Please request a new one.');
        setBusy(false);
        return;
      }

      await setActive({ session: result.createdSessionId });
      const outcome = await completeOtpSession();
      if (outcome.success) {
        onSignedIn?.(outcome);
      } else {
        // The code was right but the app could not open a session. Keep the reason on screen and
        // let go of the Clerk session, so a retry starts clean instead of hitting
        // "you're already signed in".
        setError(outcome.message || 'We could not finish signing you in');
        setStep('email');
        setCode('');
        await setActive({ session: null }).catch(() => {});
      }
    } catch (verifyError) {
      // Clerk refuses a second attempt once a session exists. That session is still good proof,
      // so use it rather than leaving the person stuck on "you're already signed in".
      const alreadySignedIn = /already signed in|identifier_already_signed_in/i.test(
        [verifyError?.message, ...(verifyError?.errors || []).map((e) => `${e.code} ${e.message}`)]
          .filter(Boolean)
          .join(' ')
      );
      if (alreadySignedIn) {
        const outcome = await completeOtpSession();
        if (outcome.success) {
          onSignedIn?.(outcome);
          setBusy(false);
          return;
        }
        setError(outcome.message || 'We could not finish signing you in');
        setStep('email');
        setCode('');
        await setActive({ session: null }).catch(() => {});
      } else {
        setError(readError(verifyError));
      }
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    try {
      const factor = signIn.supportedFirstFactors?.find((f) => f.strategy === 'email_code');
      await signIn.prepareFirstFactor({ strategy: 'email_code', emailAddressId: factor.emailAddressId });
      toast.success('New code sent');
    } catch (resendError) {
      setError(readError(resendError));
    } finally {
      setBusy(false);
    }
  };

  if (step === 'email') {
    return (
      <form onSubmit={sendCode} className="space-y-5" data-testid="otp-email-step">
        <div>
          <Label htmlFor="otp-email" className="text-sm font-medium mb-1.5 block">
            Email address
          </Label>
          <div className="relative">
            <Envelope size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground-muted" />
            <Input
              id="otp-email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="otp-email-input"
              className="pl-11 h-12 bg-[rgb(var(--background))] border-black/[0.08] rounded-xl focus:border-primary focus:ring-primary"
            />
          </div>
          <p className="text-xs text-foreground-muted mt-2">
            We send a 6-digit code. No password to remember, nothing to leak.
          </p>
        </div>

        {error && (
          <p className="text-sm text-red-600" data-testid="otp-error">
            {error}
          </p>
        )}

        <Button
          type="submit"
          disabled={busy || !isLoaded}
          className="w-full bg-primary hover:bg-primary-hover text-white rounded-full h-12 text-base font-semibold transition-all hover:shadow-lg hover:shadow-primary/25"
          data-testid="otp-send-code-button"
        >
          {busy ? 'Sending code...' : 'Send me a code'}
          {!busy && <ArrowRight size={18} className="ml-2" />}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={verify} className="space-y-5" data-testid="otp-code-step">
      <div>
        <Label htmlFor="otp-code" className="text-sm font-medium mb-1.5 block">
          6-digit code
        </Label>
        <Input
          id="otp-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="123456"
          value={code}
          onChange={(e) => setCode(codeDigits(e.target.value))}
          required
          data-testid="otp-code-input"
          className="h-14 text-center text-2xl tracking-[0.4em] font-mono bg-[rgb(var(--background))] border-black/[0.08] rounded-xl focus:border-primary focus:ring-primary"
        />
        <p className="text-xs text-foreground-muted mt-2" data-testid="otp-sent-to">
          Sent to {email}
          {isNewAccount ? ' — this code also creates your account.' : '.'}
        </p>
      </div>

      {error && (
        <p className="text-sm text-red-600" data-testid="otp-error">
          {error}
        </p>
      )}

      <Button
        type="submit"
        disabled={busy}
        className="w-full bg-primary hover:bg-primary-hover text-white rounded-full h-12 text-base font-semibold transition-all hover:shadow-lg hover:shadow-primary/25"
        data-testid="otp-verify-button"
      >
        {busy ? 'Checking...' : 'Sign in'}
        {!busy && <ArrowRight size={18} className="ml-2" />}
      </Button>

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={() => {
            setStep('email');
            setCode('');
            setError('');
          }}
          className="inline-flex items-center gap-1.5 text-foreground-muted hover:text-foreground transition-colors"
          data-testid="otp-change-email-button"
        >
          <ArrowLeft size={14} /> Use another email
        </button>
        <button
          type="button"
          onClick={resend}
          disabled={busy}
          className="text-primary hover:underline font-medium disabled:opacity-50"
          data-testid="otp-resend-button"
        >
          Resend code
        </button>
      </div>
    </form>
  );
};
